import { critTypes } from '../../Probability/ProbabilityHome.constant';

function getBinomialCoefficientHelper(n: number, k: number, memo: number[][]): number {
  if(k > n)
    return 0;
  
  if(k === 0 || k === n)
    return 1;
  
  if(memo[n][k] !== -1)
    return memo[n][k];
  
  return memo[n][k] = getBinomialCoefficientHelper(n -1, k - 1, memo) + getBinomialCoefficientHelper(n - 1, k, memo);
}
  
function getBinomialCoefficient(n: number, k: number): number {
  const memo: number[][] = Array.from({ length: n + 1 }, () => Array(k + 1).fill(-1));
  return getBinomialCoefficientHelper(n, k, memo);
}
  
export function getProbabilityDistribution(numberOfIterations: number, chance: number): number[] {
  const dist = Array(numberOfIterations + 1).fill(0);
  
  for(let i = 0; i < numberOfIterations + 1; i += 1) {
    dist[i] = getBinomialCoefficient(numberOfIterations, i) * chance ** i * (1 - chance) ** (numberOfIterations - i);
  }
      
  return dist;
}
  
export function getWardDistribution(woundDist: number[], damage: number, ward: number): number[] {
  const wardChance = 1 - (7 - ward) / 6.0;
  const totalPossibleDamage = Math.ceil(woundDist.length * damage);
  const output: number[] = Array(totalPossibleDamage).fill(0);
  
  for(let i = 0; i < woundDist.length; i += 1) {
    const dist = getProbabilityDistribution(Math.floor(i * damage), wardChance);
    dist.forEach((elem, j) => {
      output[j] += elem * woundDist[i];
    });
  }
  
  return output;
}
  
export function getProbabilityOfDamageOrHigher(dist: number[], damage: number) {
  let output = 0;
  for(let i = damage; i < dist.length; i += 1) {
    output += dist[i];
  }
  return output;
}
  
export function getProbabilityOfDamageOrLower(dist: number[], damage: number) {
  let output = 0;
  for(let i = 0; i <= damage; i += 1) {
    output += dist[i];
  }
  return output;
}

export function getWoundProbability(hit: number, wound: number, rend: number, critType: string, save: number, isEthereal: boolean) {
  const toWound = (7 - wound) / 6;
  const toFailSave =  1 - Math.max((7 - save - (isEthereal ? 0 : rend)) / 6, 0);
  
  if (critType === critTypes[1]) {
    return (1 + toWound * toFailSave * (6 - hit)) / 6;
  } else if (critType === critTypes[2]) {
    return (1 + toWound * (6 - hit)) / 6 * toFailSave;
  } else if (critType === critTypes[3]) {
    return (2 + (6 - hit)) / 6 * toWound * toFailSave;
  } else {
    const toHit = (7 - hit) / 6;

    return toHit * toWound * toFailSave;
  }
}

export function getAllCombosOfDamage(damage: number, maxX: number, maxY: number): [number, number][] {
  if(maxX + maxY < damage) {
    return [];
  }

  const output: [number, number][] = [];

  for(let i = 0; i <= damage && i <= maxX; i += 1) {
    const diff = damage - i;
    if(diff <= maxY && diff >= 0) {
      output.push([i, diff]);
    }
  }

  return output;
}

export function getSumProbability(sum: number, numberOfDice: number, diceSize: number) {
  const m = Math.floor((sum - numberOfDice) / diceSize);

  let total = 0;
  for(let k = 0; k <= m; k += 1) {
    total += (-1) ** k * getBinomialCoefficient(numberOfDice, k) * getBinomialCoefficient(sum - k * diceSize - 1, numberOfDice - 1);
  }

  return total / diceSize ** numberOfDice;
}

export function getSumProbabilityDistribution(numberOfDice: number, diceSize: number) {
  const maxSum = numberOfDice * diceSize + 1;
  const dist = Array(maxSum).fill(0.0);

  for(let i = numberOfDice; i < maxSum; i += 1) {
    dist[i] = getSumProbability(i, numberOfDice, diceSize);
  }

  return dist;
}

export function combineDependentSumDist(dist: number[], diceSize: number) {
  const maxSum = (dist.length - 1) * diceSize;

  const output = Array(maxSum + 1).fill(0.0);
  output[0] = dist[0];

  for(let i = 1; i < dist.length; i += 1) {
    const sumDist = getSumProbabilityDistribution(i, diceSize);
    sumDist.forEach((elem, j) => {
      output[j] += elem * dist[i];
    });
  }

  return output;
}

export function getDiceDetails(s: string) {
  const split = s.split('+');
  const diceValues = split[0].split('D');
  const diceSize = Number(diceValues.length > 1 ? diceValues[1] : diceValues[0]);
  const numDice =  Number(diceValues[0] !== '' ? diceValues[0] : 1);
  const modifier = split.length > 1 ? Number(split[1]) : 0;

  return { numDice, diceSize, modifier};
}